namespace $ {

	type $shm_hitalama_mcp_client_request = {
		type: 'request'
		id: string
		method: string
		params?: Record< string, any >
		target?: {
			client_id?: string
			map_id?: string
			session_id?: string
		}
	}

	const $shm_hitalama_mcp_client_timeout = Symbol( 'hitalama_mcp_client_timeout' )

	export class $shm_hitalama_mcp_client extends $mol_object {

		private socket: WebSocket | null = null
		private reconnecting = false
		private registering = false

		app() {
			return null as any as $.$$.$shm_hitalama_app
		}

		url() {
			const url = this.$.$mol_state_local.value< string >( 'hitalama_mcp_url' ) ?? this.url_default()
			const token = this.$.$shm_hitalama_settings.agent_token()
			if( !token ) return url

			const parsed = new URL( url, this.$.$mol_dom_context.document?.location.href )
			parsed.searchParams.set( 'token', token )
			return parsed.toString()
		}

		url_default() {
			const location = this.$.$mol_dom_context.document?.location
			if( location?.protocol === 'https:' && location.hostname.startsWith( 'hitalama.' ) ) {
				return `wss://mcp.${ location.hostname.slice( 9 ) }/hitalama-mcp`
			}
			return 'ws://127.0.0.1:4817/hitalama-mcp'
		}

		client_id( next?: string ) {
			const key = '$' + 'hitalama_mcp_client.client_id'
			const state = this.$.$mol_state_session
			const stored = state.value< string >( key, next )
			if( stored ) return stored
			const id = `tab_${ Date.now().toString( 36 ) }_${ Math.random().toString( 36 ).slice( 2 ) }`
			state.value( key, id )
			return id
		}

		auto() {
			this.connect()
			this.register_later()
			return null
		}

		connect() {
			if( typeof WebSocket === 'undefined' ) return
			if( this.socket?.readyState === WebSocket.OPEN ) return
			if( this.socket?.readyState === WebSocket.CONNECTING ) return

			const socket = this.socket = new WebSocket( this.url() )
			socket.onopen = ()=> this.register()
			socket.onmessage = event => $mol_wire_async( this ).message( event )
			socket.onclose = ()=> this.reconnect_later()
			socket.onerror = ()=> socket.close()
		}

		reconnect_later() {
			this.socket = null
			if( this.reconnecting ) return
			this.reconnecting = true
			new this.$.$mol_after_timeout( 1500, ()=> {
				this.reconnecting = false
				this.connect()
			} )
		}

		register_later() {
			if( this.registering ) return
			this.registering = true
			new this.$.$mol_after_timeout( 2000, ()=> {
				this.registering = false
				this.register()
				this.register_later()
			} )
		}

		send( message: Record< string, any > ) {
			const socket = this.socket
			if( !socket || socket.readyState !== WebSocket.OPEN ) return
			socket.send( JSON.stringify( message ) )
		}

		register() {
			this.send({
				type: 'register',
				... this.client_info(),
			})
		}

		client_info() {
			const app = this.app()
			const map = this.map()
			const selected = this.selected_links()
			return {
				client_id: this.client_id(),
				map_id: map?.link().str ?? '',
				map_title: map?.title?.() ?? '',
				url: this.$.$mol_dom_context.document?.location.href ?? '',
				focused: this.$.$mol_dom_context.document?.hasFocus?.() ?? false,
				visible: this.$.$mol_dom_context.document?.visibilityState !== 'hidden',
				selected_count: selected.length,
				spread: app.spread?.() ?? '',
			}
		}

		map() {
			const id = this.app().map_id?.() ?? ''
			if( !id ) return null
			return this.$.$giper_baza_glob.Pawn( new this.$.$giper_baza_link( id ), this.$.$shm_hitalama_map )
		}

		map_page() {
			const id = this.app().map_id?.() ?? ''
			if( !id ) return null
			try {
				return this.app().Maps().Book( id ).Map_page() as $shm_hitalama_map_page
			} catch( error ) {
				return null
			}
		}

		selected_links() {
			return this.map_page()?.selected_links?.() ?? []
		}

		message( event: MessageEvent ) {
			const request = JSON.parse( String( event.data ) ) as $shm_hitalama_mcp_client_request
			if( request.type !== 'request' ) return
			this.answer( request )
		}

		async answer( request: $shm_hitalama_mcp_client_request ) {
			try {
				this.request_target_check( request )
				const result = await this.$.$mol_wire_async( this ).handle( request.method, request.params ?? {} )
				this.send({
					type: 'response',
					id: request.id,
					result,
				})
			} catch( error ) {
				this.send({
					type: 'response',
					id: request.id,
					error: {
						message: error instanceof Error ? error.message : String( error ),
					},
				})
			}
		}

		request_target_check( request: $shm_hitalama_mcp_client_request ) {
			const target = request.target
			if( !target ) return

			const client_id = target.client_id ?? ''
			if( client_id && client_id !== this.client_id() ) {
				throw new Error( `Hitalama MCP request is bound to client "${ client_id }", but this browser client is "${ this.client_id() }"` )
			}

			const map_id = target.map_id ?? ''
			if( !map_id ) return

			const current = this.app().map_id?.() ?? ''
			if( current === map_id ) return

			throw new Error( `Hitalama MCP request is bound to map "${ map_id }", but this browser client has map "${ current || 'none' }" open` )
		}

		handle( method: string, params: Record< string, any > ) {
			if( method === 'map_context' ) return this.map_context()
			if( method === 'selected_widgets' ) return this.selected_widgets()
			if( method === 'widgets' ) return this.widgets()
			if( method === 'widget_list' ) return this.widgets()
			if( method === 'widget_get' ) return this.widget_info( params.id, true )
			if( method === 'widget_text' ) return this.widget_text( params.id )
			if( method === 'widget_create' ) return this.widget_create( params )
			if( method === 'widget_update' ) return this.widget_update( params.id, params )
			if( method === 'widget_move_resize' ) return this.widget_move_resize( params.id, params )
			if( method === 'widget_open_view' ) return this.widget_open_view( params.id, params )
			if( method === 'widget_result' ) return this.widget_result( params.id, params )
			if( method === 'widget_delete' ) return this.widget_delete( params.id )
			if( method === 'widget_cut' ) return this.widget_delete( params.id )
			if( method === 'map_store_get' ) return this.map_store_get( params.key )
			if( method === 'map_store_set' ) return this.map_store_set( params.key, params.value )
			if( method === 'execute_js' ) return this.execute_js( params.code )
			throw new Error( `Unknown Hitalama bridge method "${ method }"` )
		}

		map_context() {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return {
				... this.client_info(),
				widgets_count: this.widgets().length,
				selected_widgets: this.selected_widgets(),
			}
		}

		widgets() {
			return this.map()?.Widgets_list()?.remote_list().map( ( widget: $shm_hitalama_map_widget ) => this.widget_info_from( widget, false ) ) ?? []
		}

		selected_widgets() {
			const map = this.map()
			return this.selected_links().map( link => {
				const widget = map?.widget_by_link( link )
				return widget ? this.widget_info_from( widget, true ) : { id: link.str }
			} )
		}

		widget_by_id( id: string ) {
			if( !id ) throw new Error( 'Widget id is required' )
			const widgets = this.map()?.Widgets_list()?.remote_list() ?? []
			return widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.link().str === id )
				?? widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.title() === id )
				?? widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.title().toLowerCase() === id.toLowerCase() )
		}

		widget_text( id: string ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			return widget.text()
		}

		widget_info( id: string, text: boolean ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			return this.widget_info_from( widget, text )
		}

		widget_info_from( widget: $shm_hitalama_map_widget, text: boolean ) {
			return {
				id: widget.link().str,
				title: widget.title(),
				type: widget.type(),
				x: widget.body_x(),
				y: widget.body_y(),
				width: widget.right_edge_x(),
				height: widget.bottom_edge_y(),
				opacity: widget.opacity(),
				selected: this.selected_links().some( link => link.str === widget.link().str ),
				text: text ? widget.text() : undefined,
			}
		}

		widget_create( params: Record< string, any > ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )

			const type = String( params.type || 'text' )
			const x = Number( params.x ?? 0 )
			const y = Number( params.y ?? 0 )
			const width = Number( params.width ?? 320 )
			const height = Number( params.height ?? 180 )
			const title = params.title == null ? undefined : String( params.title )
			const text = params.text == null ? '' : String( params.text )
			const widget = map.widget_add( type as any, [ x, y ], width, height, title, text )
			if( !widget ) throw new Error( `Widget "${ type }" was not created` )
			if( params.data !== undefined ) widget.data( params.data )
			if( params.enabled !== undefined ) widget.enabled( Boolean( params.enabled ) )

			this.register()
			return this.widget_info_from( widget, true )
		}

		widget_update( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )

			if( params.title !== undefined ) widget.title( String( params.title ) )
			if( params.text !== undefined ) widget.text( String( params.text ) )
			if( params.type !== undefined ) widget.type( String( params.type ) as any )
			if( params.data !== undefined ) widget.data( params.data )
			if( params.enabled !== undefined ) widget.enabled( Boolean( params.enabled ) )
			if( params.opacity !== undefined ) widget.opacity( Number( params.opacity ) )
			this.widget_move_resize_optional( widget, params )

			this.register()
			return this.widget_info_from( widget, true )
		}

		widget_move_resize( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			this.widget_move_resize_optional( widget, params )
			this.register()
			return this.widget_info_from( widget, true )
		}

		widget_move_resize_optional( widget: $shm_hitalama_map_widget, params: Record< string, any > ) {
			if( params.x !== undefined ) widget.body_x( Number( params.x ) )
			if( params.y !== undefined ) widget.body_y( Number( params.y ) )
			if( params.width !== undefined ) widget.right_edge_x( Number( params.width ) )
			if( params.height !== undefined ) widget.bottom_edge_y( Number( params.height ) )
		}

		widget_open_view( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )

			const view = widget.code_view_widget_open()
			if( !view ) throw new Error( `Widget "${ widget.title() || id }" does not support a view widget` )
			if( params.title !== undefined ) view.title( String( params.title ) )
			this.widget_move_resize_optional( view, params )

			this.register()
			return this.widget_info_from( view, true )
		}

		code_source_widget( widget: $shm_hitalama_map_widget ) {
			const code = widget.code_widget()
			if( code ) return code
			if( widget.code_view_widget() ) return widget
			return null
		}

		async widget_result( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )

			const source = this.code_source_widget( widget )
			if( !source ) throw new Error( `Widget "${ widget.title() || id }" does not support code result` )

			let view: $shm_hitalama_map_widget | null | undefined
			if( params.open_view !== false ) view = source.code_view_widget_open()
			else view = source.view_widget()

			const runner = source.map().runtime().widget_runner( source.link_str() )
			const wait_ms = Math.max( 0, Number( params.wait_ms ?? 8000 ) )
			let result: unknown
			let error: unknown
			let timed_out = false

			if( params.run !== false ) {
				const run = this.$.$mol_wire_async( runner ).run_auto( null )
				Promise.resolve( run ).catch( ()=> null )

				try {
					result = wait_ms
						? await this.promise_timeout( run, wait_ms )
						: await run
				} catch( next ) {
					if( next === $shm_hitalama_mcp_client_timeout ) timed_out = true
					else error = next
				}
			}

			const process = this.$.$shm_hitalama_process.last_named( runner, 'run_auto' )
			const data = process?.data()
			if( error === undefined && data?.status === 'failed' ) error = data.error
			if( result === undefined && !timed_out && data?.status === 'done' ) result = data.result
			if( error instanceof Error ) source.map().runtime().runtime_sourcemap().error_stack_remap( error )

			this.register()
			return {
				ok: error === undefined && !timed_out,
				status: timed_out ? 'running' : data?.status ?? ( params.run === false ? 'idle' : 'done' ),
				source: this.widget_info_from( source, false ),
				view: view ? this.widget_info_from( view, false ) : null,
				result: error === undefined ? this.result_json( result ) : undefined,
				error: error === undefined ? undefined : this.error_json( error ),
				started_at: data?.started_at?.toString?.() ?? undefined,
				finished_at: data?.finished_at?.toString?.() ?? undefined,
				duration_ms: process?.duration?.(),
				runs_count: data?.runs_count,
				timed_out,
			}
		}

		promise_timeout< Value >( promise: PromiseLike< Value >, ms: number ) {
			return new Promise< Value >( ( resolve, reject )=> {
				const timer = setTimeout( ()=> reject( $shm_hitalama_mcp_client_timeout ), ms )
				Promise.resolve( promise ).then(
					value => {
						clearTimeout( timer )
						resolve( value )
					},
					error => {
						clearTimeout( timer )
						reject( error )
					},
				)
			} )
		}

		widget_delete( id: string ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			map.widget_cut( widget.link() )
			this.register()
			return { ok: true, id: widget.link().str }
		}

		map_store_get( key: string ) {
			if( !key ) throw new Error( 'Store key is required' )
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return map.store( key )
		}

		map_store_set( key: string, value: any ) {
			if( !key ) throw new Error( 'Store key is required' )
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return map.store( key, value )
		}

		async execute_js( code: string ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			if( !code ) throw new Error( 'Code is required' )

			const result = await this.$.$mol_wire_async( map ).execute_action( code, { [ '$' + 'page' ]: this.map_page() } )
			this.register()
			return this.result_json( result )
		}

		result_json( value: any ): any {
			if( value == null ) return value
			if( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) return value
			if( value instanceof Element ) return {
				type: 'Element',
				tag: value.tagName.toLowerCase(),
				text: value.textContent?.slice( 0, 500 ) ?? '',
			}
			if( value instanceof this.$.$mol_view ) return {
				type: '$mol_view',
				class: value.constructor.name,
			}

			try {
				return JSON.parse( JSON.stringify( value ) )
			} catch( error ) {
				return String( value )
			}
		}

		error_json( error: unknown ): any {
			if( this.$.$mol_promise_like( error ) ) return {
				name: 'Promise',
				message: 'Async result is still pending',
			}

			if( error instanceof Error ) return {
				name: error.name,
				message: this.$.$mol_error_message( error ),
				stack: this.error_stack_text( error ),
			}

			return {
				name: typeof error,
				message: String( error ),
			}
		}

		error_stack_text( error: Error ) {
			const remapped = ( error as { $shm_hitalama_map_runtime_stack?: unknown } ).$shm_hitalama_map_runtime_stack
			if( typeof remapped === 'string' ) return remapped

			const stack = ( error as { stack?: unknown } ).stack
			if( typeof stack === 'string' ) return stack
			if( Array.isArray( stack ) ) return stack.map( frame => this.error_frame_text( frame ) ).join( '\n' )

			return undefined
		}

		error_frame_text( frame: any ) {
			const title = frame?.function || frame?.method || '<anonymous>'
			const source = frame?.source || frame?.eval || ''
			const pos = Array.isArray( frame?.pos )
				? `:${ Number( frame.pos[0] ) + 1 }:${ Number( frame.pos[1] ) + 1 }`
				: ''

			return source ? `${ title } @ ${ source }${ pos }` : title
		}

		override destructor() {
			const socket = this.socket
			if( socket ) {
				socket.onopen = null
				socket.onmessage = null
				socket.onclose = null
				socket.onerror = null
				socket.close()
			}
			this.socket = null
			super.destructor()
		}

	}

}
